<script setup>
import ga4 from '#common/utils/ga4'
import {
  filtersToUrl,
  positionSorter,
  simpleFiltersToUrl,
  urlToFilters,
} from '#wa/utils/filters/filters'

import Accordion from '#common/components/Accordion/Accordion'
import AccordionItem from '#common/components/Accordion/AccordionItem'

// directives: {
//   ClickOutside,
// },
const props = defineProps({
  excluded: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  base: {
    type: String,
    required: false,
    default: null,
  },
  simpleUrls: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const { $store, $auth } = useNuxtApp()
const $route = useRoute()
const metadata = useMetadata()
const productResults = useProductResults()
const sortMenuOpen = ref(false)
const hideExtraVarietals = ref(true)
const mobileActive = ref(false)
const mobileTempFilters = ref(null)
const savedSearchList = computed(() => $store.getters.savedSearchList)
const showSavedSearch = computed(() => {
  return $auth.loggedIn && metadata.constance.S3_SAVED_SEARCH_ENABLED === 'True'
})

const urlFilters = computed(() => urlToFilters({ route: $route }))
const selectedFilters = computed(() => {
  const filters = getAllCurrentFilters().filter(
    (f) => !['ordering', 'page', 'region_slug'].includes(f.category),
  )
  const regionChips = metadata.selectedRegionChips.map((chipName) => ({
    chipName,
    name: chipName,
    category: 'region_slug',
  }))
  return filters.concat(regionChips)
})
const selectedFiltersFromUrl = computed(() =>
  getAllCurrentFilters().filter(
    (f) => !['ordering', 'page'].includes(f.category),
  ),
)
const relevantSortingOptions = computed(() =>
  metadata.sortingOptions.filter(
    (option) => !option.hidden || ordering.value === option.ordering,
  ),
)
const ordering = computed(() =>
  productResults.orderingFromUrl($route, selectedFiltersFromUrl.value),
)
const orderingLabel = computed(() => {
  switch (ordering.value) {
    case 'personalized':
      return 'For You'
    case '-popularity_ranking':
      return 'Most Popular'
    case '-start_datetime':
      return 'Newest'
    case 'price':
      return 'Price: low to high'
    case '-price':
      return 'Price: high to low'
    case '-expert_rating':
      return 'Critic Score'
    case '-score':
      return 'Best Match'
    default:
      return ''
  }
})
const categories = computed(() =>
  metadata
    .searchFiltersByCategory('categories')
    .map((obj, i) => ({
      ...obj,
      position: metadata.categories[obj.key].position + 1,
    }))
    .sort(positionSorter),
)
const varietals = computed(() =>
  metadata
    .searchFiltersByCategory('varietals')
    .filter(
      (v) =>
        isFilterSelected(v) ||
        v.show_in_filter_dropdown ||
        !hideExtraVarietals.value,
    )
    .sort(positionSorter),
)
const food_pairings = computed(() =>
  metadata
    .searchFiltersByCategory('food_pairings')
    .map((obj, i) => ({
      ...obj,
      position: metadata.food.find((f) => f.slug === obj.key).position,
    }))
    .sort(positionSorter),
)
const vintages = computed(() => metadata.searchFiltersByCategory('vintage'))
const expert_reviewers = computed(() => {
  const filterableExperts = metadata.expert_reviewers
    .filter((expert) => expert.filterable)
    .reduce((prev, curr) => {
      prev[curr.slug] = curr
      return prev
    }, {})
  function expertSorter(a, b) {
    const filterableA = filterableExperts[a.key]
    const filterableB = filterableExperts[b.key]
    if (
      (!filterableA || !filterableA.position) &&
      (!filterableB || !filterableB.position)
    )
      return 0
    if (!filterableA || !filterableA.position) return 1
    if (!filterableB || !filterableB.position) return -1
    return filterableA.position - filterableB.position
  }
  return metadata
    .searchFiltersByCategory('expert_reviewers')
    .filter((x) => !!filterableExperts[x.key])
    .sort(expertSorter)
})
const occasionSlugs = computed(() =>
  metadata.searchFiltersByCategory('occasions').map((x) => x.key),
)
const clearFiltersUrl = computed(() => {
  const filters = $route.query.ordering
    ? [{ category: 'ordering', key: $route.query.ordering }]
    : []
  return getUrlFromFilters(filters)
})

function onSortChange(option) {
  ga4.track('sort_item_list', {
    ordering: option.ordering,
    name: option.name,
  })
  sortMenuOpen.value = false
}
function conditionalLinkPath() {
  if (/^\/checkout\/add-to-order\/\d+\//.test($route.path)) {
    return $route.path
  } else {
    return '/store/'
  }
}
function clickOutsideSort() {
  sortMenuOpen.value = false
}
function toggleHideExtraVarietals() {
  hideExtraVarietals.value = !hideExtraVarietals.value
}
function sliderChanged(newFilter) {
  const currentFilters = getAllCurrentFilters()
  const newFilters = currentFilters.filter(
    (f) =>
      f.category !== 'page' &&
      !(f.category === newFilter.category && f.key === newFilter.key),
  )
  newFilters.push(newFilter)
  commitNewFilters(newFilters)
}
function sliderCleared(clearedFilter) {
  const currentFilters = getAllCurrentFilters()
  const newFilters = currentFilters.filter(
    (f) =>
      f.category !== 'page' &&
      !(f.category === clearedFilter.category && f.key === clearedFilter.key),
  )
  commitNewFilters(newFilters)
}
function toggleFilter(e) {
  if (e.category === 'region_slug') {
    const region = metadata.regionsIndex.find((r) => r.chipName === e.name)

    if (region) metadata.deselectRegion({ region })
    updateRegionFilters()
    return
  }
  const newFilters = [...selectedFiltersFromUrl.value]
  const filterIndex = newFilters.findIndex(
    (f) => f.category === e.category && f.key === e.key,
  )
  if (filterIndex >= 0) {
    newFilters.splice(filterIndex, 1)
    ga4.track('unfilter_item_list', e)
  } else {
    newFilters.push(e)
    ga4.track('filter_item_list', e)
  }

  commitNewFilters(newFilters)
}
function updateRegionFilters() {
  const newFilters = selectedFiltersFromUrl.value
    .filter((f) => !['region_slug', 'ordering'].includes(f.category))
    .concat(
      metadata.selectedRegionSlugs.map((slug) => ({
        category: 'region_slug',
        key: slug,
      })),
    )
    .concat({
      category: 'ordering',
      key: ordering.value,
    })

  commitNewFilters(newFilters)
}
function orderingUrl(ordering) {
  const filters = selectedFiltersFromUrl.value
  const pageIndex = filters.findIndex((f) => f.category === 'page')
  if (pageIndex !== -1) {
    filters.splice(pageIndex, 1)
  }
  const orderingFilter = filters.find((f) => f.category === 'ordering')
  if (orderingFilter) {
    orderingFilter.key = ordering
  } else {
    filters.push({ category: 'ordering', key: ordering })
  }
  return getUrlFromFilters(filters)
}
function getUrlFromFilters(filters) {
  if (props.simpleUrls) {
    return simpleFiltersToUrl(props.base, filters)
  }
  return filtersToUrl(filters)
}
function isFilterSelected(filter) {
  return !!selectedFilters.value.find((f) => {
    return f.category === filter.category && f.key === filter.key
  })
}
function getAllCurrentFilters() {
  if (mobileActive.value) {
    if (mobileTempFilters.value === null) {
      mobileTempFilters.value = urlFilters.value
    }
    return mobileTempFilters.value
  }
  return urlFilters.value
}
function commitNewFilters(newFilters) {
  if (mobileActive.value) {
    mobileTempFilters.value = newFilters
  } else {
    navigateTo(getUrlFromFilters(newFilters))
  }
}
function cancelMobileFilters() {
  mobileTempFilters.value = null
  mobileActive.value = false
}
function commitMobileFilters(onComplete) {
  mobileActive.value = false
  navigateTo(getUrlFromFilters(mobileTempFilters.value)).then(() => {
    if (typeof onComplete === 'function') {
      onComplete()
    }
  })
}
function showSavedSearchModal(editId) {
  const useEditId = typeof editId === 'number' ? editId : undefined
  if (mobileActive.value) {
    commitMobileFilters(() => {
      $store.dispatch('setSavedSearchModal', {
        editId: useEditId,
        showModal: true,
      })
    })
  } else {
    $store.dispatch('setSavedSearchModal', {
      editId: useEditId,
      showModal: true,
    })
  }
}
watch(mobileActive, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    mobileTempFilters.value = urlFilters.value.map((f) => ({ ...f }))
    document.querySelector('body').classList.add('noscroll')
  } else {
    document.querySelector('body').classList.remove('noscroll')
  }
})
</script>

<template>
  <div class="plp-filter-sidebar-container">
    <div
      id="plp-filter-sidebar"
      class="plp-filter-sidebar"
      :class="{ active: mobileActive }"
    >
      <div class="filter-scroller">
        <button
          v-if="showSavedSearch"
          class="save-search-mobile"
          :disabled="
            !(
              selectedFiltersFromUrl.length > 0 ||
              (mobileTempFilters && mobileTempFilters.length > 0)
            )
          "
          @click="showSavedSearchModal"
        >
          Save Search
        </button>
        <div v-if="showSavedSearch" class="filter-sidebar-card saved-searches">
          <Accordion
            id="filter-accordions-saved-search"
            v-slot="scope"
            :multiple="true"
          >
            <AccordionItem
              :expanded-by-default="false"
              title="My Saved Searches"
              @expanded="scope.expanded"
              ><div>
                <ul v-if="savedSearchList.length > 0" class="filter-set">
                  <li v-for="value in savedSearchList" :key="value.id">
                    <nuxt-link
                      :to="`${conditionalLinkPath()}?${decodeURIComponent(
                        value.original_query_string,
                      )}`"
                      @click="cancelMobileFilters"
                      >{{ value.name }}</nuxt-link
                    >
                    <button @click="showSavedSearchModal(value.id)">
                      <span class="sr-only">Edit</span>
                    </button>
                  </li>
                </ul>
                <p v-else class="saved-search-tip">
                  After you filter your results, you can save your search and
                  set up an alert.
                </p>
              </div>
            </AccordionItem>
          </Accordion>
        </div>
        <FilterAccordions
          :use-selected-filters="true"
          :excluded="excluded"
          :categories="categories"
          :varietals="varietals"
          :hide-extra-varietals="hideExtraVarietals"
          :food-pairings="food_pairings"
          :vintages="vintages"
          :expert-reviewers="expert_reviewers"
          :occasion-slugs="occasionSlugs"
          :current-filters="getAllCurrentFilters()"
          @sliderChanged="sliderChanged"
          @sliderCleared="sliderCleared"
          @toggleFilter="toggleFilter"
          @updateRegionFilters="updateRegionFilters"
          @toggleHideExtraVarietals="toggleHideExtraVarietals"
        />
        <div class="plp-filter-footer">
          <div class="row">
            <div class="columns small-6">
              <button @click="cancelMobileFilters">Cancel</button>
            </div>
            <div class="columns small-6">
              <button class="apply" @click="commitMobileFilters">
                Save and View
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="plp-filter-main">
      <div class="applied-sort-container">
        <div class="mobile-filters">
          <button
            class="mobile-filter-trigger"
            aria-controls="plp-filter-sidebar"
            :aria-expanded="mobileActive"
            @click="mobileActive = !mobileActive"
          >
            Filters
          </button>
        </div>
        <SelectedFilterChips
          :selected-filters="selectedFilters"
          :clear-filters-url="clearFiltersUrl"
          :hide-save="!showSavedSearch"
          @filterToggled="toggleFilter"
        />
        <div class="plp-sort">
          <div
            v-click-outside="clickOutsideSort"
            role="region"
            class="sort-option"
          >
            <button
              class="sort-trigger"
              :class="{ active: sortMenuOpen }"
              aria-haspopup="true"
              :aria-expanded="sortMenuOpen"
              aria-controls="sort_menu"
              @click="sortMenuOpen = !sortMenuOpen"
            >
              <span id="sort_by">Sort by:&nbsp;</span>
              <span class="sr-only">Currently sorting by</span>
              <strong>{{ orderingLabel }}</strong>
            </button>
            <div
              id="sort_menu"
              class="submenu"
              :class="{ active: sortMenuOpen }"
              aria-labelledby="sort_by"
            >
              <div class="submenu-content">
                <ul role="menu">
                  <li
                    v-for="[index, option] in relevantSortingOptions.entries()"
                    :id="'sort_by_' + index"
                    :key="'sort_by_' + index"
                  >
                    <nuxt-link
                      :class="ordering === option.ordering ? 'active' : ''"
                      :to="orderingUrl(option.ordering)"
                      role="menuitem"
                      @click="onSortChange(option)"
                    >
                      {{ option.name }}
                    </nuxt-link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <slot><!-- Main content goes here --></slot>
    </div>
  </div>
</template>
